import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Typography } from '@app/ui';

type PostsCampaignAssignmentMenuProps = {
    postCount: number;
    selectedPostsCount: number;
    isPostSelectionEnabled: boolean;
    onDisplaySelectionCheckboxChange: (
        postSelectionAvailability: boolean,
    ) => void;
    shouldDisableButtons: boolean;
    setPostsMatchingFiltersSelected: (selected: boolean) => () => void;
    isPostsMatchingFiltersSelected: boolean;
    excludedIdsCount: number;
    onDisplayCampaignAssigmentModal: () => void;
    isCampaignAssignAvailable: boolean;
};

const PostsCampaignAssignmentMenu = ({
    postCount,
    selectedPostsCount,
    isPostSelectionEnabled,
    onDisplaySelectionCheckboxChange,
    shouldDisableButtons,
    setPostsMatchingFiltersSelected,
    isPostsMatchingFiltersSelected,
    onDisplayCampaignAssigmentModal,
    isCampaignAssignAvailable,
    excludedIdsCount,
}: PostsCampaignAssignmentMenuProps) => {
    const { t } = useTranslation('ownedSocial');
    return (
        <>
            {!isPostSelectionEnabled && (
                <Button
                    type="primary"
                    onClick={() => onDisplaySelectionCheckboxChange(true)}
                    data-test="assign-posts"
                    disabled={shouldDisableButtons}
                    icon={<i className="fa-regular fa-memo -m-r-1" />}
                >
                    {t('socialPostMetrics.assignPosts')}
                </Button>
            )}
            {isPostSelectionEnabled && (
                <Flex gap={8} flex={1}>
                    <Button
                        type="primary"
                        color="default"
                        variant="solid"
                        onClick={() => onDisplaySelectionCheckboxChange(false)}
                        disabled={shouldDisableButtons}
                        data-test="cancel-post"
                        icon={<i className="fa-regular fa-x" />}
                    >
                        {t('socialPostMetrics.cancel')}
                    </Button>
                    <Button
                        type="primary"
                        color="primary"
                        variant="outlined"
                        onClick={setPostsMatchingFiltersSelected(true)}
                        disabled={shouldDisableButtons}
                        data-test="select-all"
                        icon={<i className="fa-regular fa-square-check" />}
                    >
                        {t('socialPostMetrics.selectAll')}
                    </Button>
                    <Button
                        type="primary"
                        color="default"
                        variant="solid"
                        onClick={setPostsMatchingFiltersSelected(false)}
                        disabled={
                            shouldDisableButtons ||
                            (isPostsMatchingFiltersSelected && !postCount) ||
                            (!isPostsMatchingFiltersSelected &&
                                !selectedPostsCount) ||
                            !isCampaignAssignAvailable
                        }
                        data-test="unselect-all"
                        icon={<i className="fa-regular fa-square" />}
                    >
                        {t('socialPostMetrics.unselectAll')}
                    </Button>
                    {isCampaignAssignAvailable && (
                        <Button
                            type="primary"
                            onClick={onDisplayCampaignAssigmentModal}
                            disabled={shouldDisableButtons}
                            data-test="assign-campaign"
                            icon={<i className="fa-solid fa-folder-open" />}
                        >
                            <Typography.Text
                                style={{
                                    color: 'white',
                                }}
                            >
                                {(() => {
                                    const count = isPostsMatchingFiltersSelected
                                        ? postCount - excludedIdsCount
                                        : selectedPostsCount;
                                    const shouldBold =
                                        (isPostsMatchingFiltersSelected &&
                                            postCount > 0) ||
                                        (!isPostsMatchingFiltersSelected &&
                                            selectedPostsCount > 0);
                                    const postText =
                                        count !== 1
                                            ? t('socialPostMetrics.posts')
                                            : t('socialPostMetrics.post');
                                    return (
                                        <>
                                            {t(
                                                'socialPostMetrics.modal.assign',
                                            )}
                                            {` `}
                                            <span
                                                style={{
                                                    fontWeight: shouldBold
                                                        ? 'bold'
                                                        : 'normal',
                                                }}
                                            >
                                                ({count})
                                            </span>
                                            {` `}
                                            {postText}{' '}
                                            {t(
                                                'socialPostMetrics.modal.toCampaign',
                                            )}
                                        </>
                                    );
                                })()}
                            </Typography.Text>
                        </Button>
                    )}
                </Flex>
            )}
        </>
    );
};

export default PostsCampaignAssignmentMenu;
